import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormErrorMessage } from './FormErrorMessage'
import prefectures from '../data/prefectures'

const reCAPTCHASiteKey = process.env.GATSBY_SITE_RECAPTCHA_KEY

// 表示のみ英語化し、内部データは日本語のまま取り扱う
function ContactFormInputEn({
  onConfirm,
  privacyPolicyPath,
  recruitmentPath,
  className,
  inquiryTypes,
  requireAddress,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useFormContext()

  const recaptchaRef = React.useRef()
  const onVerifyCaptcha = (token) => {
    // expire した場合には null が返ってくる
    setValue('g-recaptcha-response', token ?? '', { shouldValidate: true })
  }

  // 「住所」は、「お問い合わせ種別」に応じて表示する
  const selectedInquiryTypes = useWatch({ name: 'inquiryType' })
  const showAddressFields = requireAddress(selectedInquiryTypes)

  // submitHandlers
  const onValid = () => {
    onConfirm()
  }

  return (
    <>
      <h2 className={className}>Inquiry form</h2>
      <div className="inquiry_area">
        <p className="note">
          Fields marked with <span className="red">※</span> are required.
        </p>
        <form name="contact" method="post" onSubmit={handleSubmit(onValid)}>
          <input type="hidden" {...register('g-recaptcha-response')} />
          <input type="hidden" {...register('csrftoken')} />
          <table className="contact">
            <tbody>
              <tr id="Cnt1">
                <th className="must">Inquiry type</th>
                <td className="checkbox_square">
                  <fieldset>
                    {inquiryTypes.map((type, index) => (
                      <label key={index}>
                        <input
                          type="checkbox"
                          {...register('inquiryType')}
                          value={type}
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </fieldset>
                  <FormErrorMessage error={errors.inquiryType} />
                  <div className="recruitment-link">
                    For inquiries about employment, please contact us{' '}
                    <a href={recruitmentPath}>here</a>.
                  </div>
                </td>
              </tr>
              <tr id="Cnt2">
                <th className="must">Name (Kanji)</th>
                <td className="name">
                  <span>
                    <input
                      type="text"
                      {...register('nameKanji')}
                      placeholder="e.g. 藤元太郎"
                    />
                  </span>
                  <FormErrorMessage error={errors.nameKanji} />
                </td>
              </tr>
              <tr id="Cnt3">
                <th className="must">Name (Furigana)</th>
                <td className="name">
                  <span>
                    <input
                      type="text"
                      {...register('nameFurigana')}
                      placeholder="e.g. フジモトタロウ"
                    />
                  </span>
                  <FormErrorMessage error={errors.nameFurigana} />
                </td>
              </tr>
              <tr id="Cnt4" hidden={!showAddressFields}>
                <th className="must">Contact address</th>
                <td className="address">
                  <div className="address_list">
                    <label>
                      <span>Postal code</span>
                      <input
                        type="tel"
                        {...register('postalCode')}
                        className="p-postal-code"
                        placeholder="e.g. 889-1911"
                      />
                    </label>
                    <FormErrorMessage error={errors.postalCode} />
                  </div>
                  <div className="address_list">
                    <label>
                      <span>Prefectures of Japan</span>
                      <select {...register('prefecture')}>
                        <option value="">Please select a prefecture</option>
                        {prefectures.map((prefecture, index) => (
                          <option key={index} value={prefecture}>
                            {prefecture}
                          </option>
                        ))}
                      </select>
                    </label>
                    <FormErrorMessage error={errors.prefecture} />
                  </div>
                  <div className="address_list">
                    <label>
                      <span>Address</span>
                      <input
                        type="text"
                        {...register('address1')}
                        className="p-address"
                      />
                    </label>
                    <FormErrorMessage error={errors.address1} />
                  </div>
                  <div className="address_list">
                    <label>
                      <span>Building name</span>
                      <input
                        type="text"
                        {...register('address2')}
                        className="p-address"
                      />
                    </label>
                    <FormErrorMessage error={errors.address2} />
                  </div>
                </td>
              </tr>
              <tr id="Cnt5">
                <th className="must">Phone number</th>
                <td className="tel">
                  <span>
                    <input
                      type="tel"
                      {...register('tel')}
                      placeholder="e.g. 0986-12-3456"
                    />
                  </span>
                  <p>
                    ※Please provide a number where we can reach you easily,
                    either a landline or cell phone.
                  </p>
                  <FormErrorMessage error={errors.tel} />
                </td>
              </tr>
              <tr id="Cnt6">
                <th className="must">Email address</th>
                <td className="mail cont">
                  <span>
                    <input
                      type="email"
                      {...register('email', {
                        deps: 'emailConfirm',
                      })}
                      placeholder="e.g. taro@fujimoto.or.jp"
                    />
                  </span>
                  <FormErrorMessage error={errors.email} />
                  <span>
                    <input
                      type="email"
                      {...register('emailConfirm')}
                      placeholder="Please enter again for confirmation"
                    />
                  </span>
                  <FormErrorMessage error={errors.emailConfirm} />
                  <p>
                    ※You can use a free email, but please make sure you can
                    receive email from @fujimoto.or.jp
                  </p>
                </td>
              </tr>
              <tr>
                <th>Inquiry details</th>
                <td className="message">
                  <span>
                    <textarea
                      {...register('inquiryBody')}
                      rows="10"
                      placeholder="Please enter your inquiry here."
                    ></textarea>
                  </span>
                  <FormErrorMessage error={errors.inquiryBody} />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="recaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={reCAPTCHASiteKey}
              onChange={onVerifyCaptcha}
            />
            <FormErrorMessage error={errors['g-recaptcha-response']} />
          </div>
          <div className="send_area">
            <p>
              The information entered will be handled in accordance with our{' '}
              <a
                href={privacyPolicyPath}
                target="_blank"
                rel="noreferrer noopener"
              >
                privacy policy
              </a>
              .
            </p>
            <button input="submit">Confirm your entry</button>
          </div>
        </form>
      </div>
    </>
  )
}

export { ContactFormInputEn }
